/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueAdvancedSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { ChangeEvent } from 'react';
import { Settings } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { FormState, TimeBaseScore } from './types';
import { TIME_BASE_SCORE_OPTIONS } from './constants';

interface QueueAdvancedSectionProps {
  formData: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  onTimeBaseScoreChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

export function QueueAdvancedSection({
  formData,
  onFieldChange,
  onTimeBaseScoreChange
}: QueueAdvancedSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('queues.form.sections.advanced')} icon={<Settings className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Time Base Score */}
        <SimpleTooltip content={t('queues.form.fields.timeBaseScore.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="timeBaseScore" className="text-left">
            {t('queues.form.fields.timeBaseScore.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <select
            id="timeBaseScore"
            value={formData.timeBaseScore}
            onChange={onTimeBaseScoreChange}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {TIME_BASE_SCORE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`queues.form.timeBaseScore.${option}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Time Base Score Seconds */}
        <SimpleTooltip content={t('queues.form.fields.timeBaseScoreSeconds.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="timeBaseScoreSeconds" className="text-left">
            {t('queues.form.fields.timeBaseScoreSeconds.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="timeBaseScoreSeconds"
            type="number"
            min="0"
            value={formData.timeBaseScoreSeconds}
            onChange={(event) => onFieldChange('timeBaseScoreSeconds', event.target.value)}
            placeholder={t('queues.form.fields.timeBaseScoreSeconds.placeholder')}
          />
        </div>

        {/* Discard Abandoned After */}
        <SimpleTooltip content={t('queues.form.fields.discardAbandonedAfter.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="discardAbandonedAfter" className="text-left">
            {t('queues.form.fields.discardAbandonedAfter.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="discardAbandonedAfter"
            type="number"
            min="0"
            value={formData.discardAbandonedAfter}
            onChange={(event) => onFieldChange('discardAbandonedAfter', event.target.value)}
            placeholder={t('queues.form.fields.discardAbandonedAfter.placeholder')}
          />
        </div>

        {/* Abandoned Resume Allowed */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queues.form.fields.abandonedResumeAllowed.tooltip')}>
            <Label htmlFor="abandonedResumeAllowed" className="text-left">
              {t('queues.form.fields.abandonedResumeAllowed.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="abandonedResumeAllowed"
            value={formData.abandonedResumeAllowed}
            onChange={(value) => onFieldChange('abandonedResumeAllowed', value)}
          />
        </div>

        {/* Exit Key */}
        <SimpleTooltip content={t('queues.form.fields.callerExitKey.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="callerExitKey" className="text-left">
            {t('queues.form.fields.callerExitKey.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="callerExitKey"
            value={formData.callerExitKey}
            onChange={(event) => onFieldChange('callerExitKey', event.target.value)}
            placeholder={t('queues.form.fields.callerExitKey.placeholder')}
            maxLength={1}
          />
        </div>

        {/* Max Wait Agents */}
        <SimpleTooltip content={t('queues.form.fields.maxWaitAgents.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="maxWaitAgents" className="text-left">
            {t('queues.form.fields.maxWaitAgents.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="maxWaitAgents"
            type="number"
            min="0"
            value={formData.maxWaitAgents}
            onChange={(event) => onFieldChange('maxWaitAgents', event.target.value)}
            placeholder={t('queues.form.fields.maxWaitAgents.placeholder')}
          />
        </div>

        {/* Max No Answer */}
        <SimpleTooltip content={t('queues.form.fields.maxNoAnswer.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="maxNoAnswer" className="text-left">
            {t('queues.form.fields.maxNoAnswer.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="maxNoAnswer"
            type="number"
            min="1"
            value={formData.maxNoAnswer}
            onChange={(event) => onFieldChange('maxNoAnswer', event.target.value)}
            placeholder={t('queues.form.fields.maxNoAnswer.placeholder')}
          />
        </div>

        {/* Queue Max Size */}
        <SimpleTooltip content={t('queues.form.fields.queueMaxSize.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="queueMaxSize" className="text-left">
            {t('queues.form.fields.queueMaxSize.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="queueMaxSize"
            type="number"
            min="0"
            value={formData.queueMaxSize}
            onChange={(event) => onFieldChange('queueMaxSize', event.target.value)}
            placeholder={t('queues.form.fields.queueMaxSize.placeholder')}
          />
        </div>

        {/* Agent No-Answer Delay */}
        <SimpleTooltip content={t('queues.form.fields.agentNoAnswerDelay.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentNoAnswerDelay" className="text-left">
            {t('queues.form.fields.agentNoAnswerDelay.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="agentNoAnswerDelay"
            type="number"
            min="0"
            value={formData.agentNoAnswerDelay}
            onChange={(event) => onFieldChange('agentNoAnswerDelay', event.target.value)}
            placeholder={t('queues.form.fields.agentNoAnswerDelay.placeholder')}
          />
        </div>

        {/* Max Wait Time with No Agent */}
        <SimpleTooltip content={t('queues.form.fields.maxWaitTimeWithNoAgent.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="maxWaitTimeWithNoAgent" className="text-left">
            {t('queues.form.fields.maxWaitTimeWithNoAgent.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="maxWaitTimeWithNoAgent"
            type="number"
            min="0"
            value={formData.maxWaitTimeWithNoAgent}
            onChange={(event) => onFieldChange('maxWaitTimeWithNoAgent', event.target.value)}
            placeholder={t('queues.form.fields.maxWaitTimeWithNoAgent.placeholder')}
          />
        </div>

        {/* Max Wait Time with No Agent Time Reached */}
        <SimpleTooltip content={t('queues.form.fields.maxWaitTimeWithNoAgentTimeReached.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="maxWaitTimeWithNoAgentTimeReached" className="text-left">
            {t('queues.form.fields.maxWaitTimeWithNoAgentTimeReached.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="maxWaitTimeWithNoAgentTimeReached"
            value={formData.maxWaitTimeWithNoAgentTimeReached}
            onChange={(event) => onFieldChange('maxWaitTimeWithNoAgentTimeReached', event.target.value)}
            placeholder={t('queues.form.fields.maxWaitTimeWithNoAgentTimeReached.placeholder')}
          />
        </div>
      </div>
    </FormBox>
  );
}

